import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Target, Wrench, Bot, Mic } from 'lucide-react';
import StreamingContentRenderer from './StreamingContentRenderer';
import VoiceListenButton from './VoiceListenButton';
import { getImageUrl } from '../config/runtime';
import { useAuth } from '../contexts/AuthContext';

// Agent message type icons and colors
const AGENT_MESSAGE_CONFIG = {
  planning: { icon: Target, label: 'Planning' },
  working: { icon: Bot, label: 'Working' },
  tools: { icon: Wrench, label: 'Tools' },
  tool_execution: { icon: Wrench, label: 'Tools' }  // Map tool_execution to same config as tools
};

const StreamingMessage = React.memo(({ 
  message, 
  streamingContent, 
  onCitationClick,
  citationRef,
  onStreamingUpdate,  // For streaming updates
  onStreamingComplete, // For streaming completion
  onLayoutChange, // For handling layout changes
  onImageClick, // New: open fullscreen viewer with spotlights
  onRetry, // Optional: retry handler for error messages
  tokenBalance = null,
  onInsufficientTokensContinue,
  onInsufficientTokensTopUp,
  // Voice layer (workstream V): { enabled, projectId, chatId } — enables the
  // per-message listen button on completed assistant messages.
  voicePlayback = null
}) => {
  const { user } = useAuth();
  const [isComplete, setIsComplete] = useState(false);
  const [hasSecurityWarning, setHasSecurityWarning] = useState(false);
  const messageContainerRef = useRef(null); // For measuring height changes
  const contentAreaRef = useRef(null); // For measuring content area
  const headerRef = useRef(null); // For scroll anchoring
  
  const [liveContent, setLiveContent] = useState(''); // For live agent message updates
  const [liveIsComplete, setLiveIsComplete] = useState(false); // Track completion state
  const [liveMessageType, setLiveMessageType] = useState(''); // Track dynamic message type during streaming
  const [liveSources, setLiveSources] = useState([]); // Track sources from live updates
  
  // Direct streaming update - throttling handled in autoscroll hook
  const triggerStreamingUpdate = useCallback(() => {
    if (onStreamingUpdate) {
      onStreamingUpdate();
    }
  }, [onStreamingUpdate]);
  
  const streamingRef = useRef(null);
  const previousContentLength = useRef(0);
  const completionTriggered = useRef(false); // Track if we've already triggered completion
  
  // Determine if this is an agent message and get its configuration
  // Use live message type if available during streaming, otherwise use static message type
  const agentMessageType = liveMessageType || message?.messageType || message?.metadata?.agent_type;
  const agentConfig = agentMessageType ? AGENT_MESSAGE_CONFIG[agentMessageType] : null;
  const isAgentMessage = !!agentConfig;
  
  // Debug logging for message type detection
  // useEffect(() => {
  //   if (message?.id) {
  //     console.log(`🔍 STREAMING MESSAGE ${message.id} type detection:`, {
  //       liveMessageType,
  //       staticMessageType: message?.messageType,
  //       metadataType: message?.metadata?.agent_type,
  //       finalType: agentMessageType,
  //       hasConfig: !!agentConfig,
  //       isAgentMessage,
  //       fullMessage: message
  //     });
  //   }
  // }, [message?.id, liveMessageType, message?.messageType, message?.metadata?.agent_type, agentMessageType, agentConfig, isAgentMessage]);


  // CONTINUOUS HEIGHT TRACKING: Update stored measurements as content grows
  useEffect(() => {
    const header = headerRef.current;
    const contentArea = contentAreaRef.current;
    if (!header || !isAgentMessage) return;

    const scrollContainer = document.querySelector('.chat-container');
    if (!scrollContainer) return;

    // Continuously update stored height as content grows (for streaming)
    if (contentArea) {
      const currentContentHeight = contentArea.offsetHeight;
      const currentScrollTop = scrollContainer.scrollTop;
      
      // Always keep the latest "expanded" measurements
      header.dataset.lastContentHeight = currentContentHeight;
      header.dataset.lastScrollTop = currentScrollTop;
      
      // console.log('📈 TRACKING growth:', { 
      //   messageId: message?.id?.slice(-8),
      //   currentContentHeight,
      //   isStreaming: !liveIsComplete
      // });
    }
  }, [liveContent, isAgentMessage, liveIsComplete, message?.id]);

  
  
  
  // Listen for live agent message updates with throttling
  useEffect(() => {
    if (!message?.id) return;

    let updateTimeout = null;
    let pendingUpdate = null;

    const handleAgentUpdate = (event) => {
      const { messageId, content, isComplete, message_type, sources, metadata } = event.detail;
      
      // Only update if this is for our message
      if (messageId === message.id) {
        // Store the pending update
        pendingUpdate = { content, isComplete, message_type, sources, metadata };
        
        // Clear existing timeout
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        
        // If complete, update immediately. Otherwise throttle to every 100ms
        if (isComplete) {
          applyUpdate(pendingUpdate);
          pendingUpdate = null;
        } else {
          updateTimeout = setTimeout(() => {
            if (pendingUpdate) {
              applyUpdate(pendingUpdate);
              pendingUpdate = null;
            }
          }, 100); // Throttle to 10 updates per second max
        }
      }
    };

    const applyUpdate = ({ content, isComplete, message_type, sources, metadata }) => {
      // Update live message type if provided (enables dynamic agent message detection)
      if (message_type) {
        setLiveMessageType(message_type);
      }
      
      setLiveContent(content || '');
      setLiveIsComplete(isComplete || false);
      
      // 🎯 FIX: Update live sources from WebSocket updates
      if (sources && Array.isArray(sources)) {
        setLiveSources(sources);
      } else if (metadata?.sources && Array.isArray(metadata.sources)) {
        setLiveSources(metadata.sources);
      }
      
      
      // Single streaming update call for auto-scroll
      triggerStreamingUpdate();
    };

    // Listen for agent message updates (now listens for all messages, not just agent messages)
    window.addEventListener('okio:agent_message_update', handleAgentUpdate);

    return () => {
      window.removeEventListener('okio:agent_message_update', handleAgentUpdate);
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [message?.id, triggerStreamingUpdate]);

  // Initialize live content from message
  useEffect(() => {
    if (isAgentMessage && message?.content) {
      setLiveContent(message.content);
      setLiveIsComplete(message.isComplete !== false); // Default to complete if not specified
      
      // Initialize live sources from message metadata
      const initialSources = message?.metadata?.sources || [];
      if (initialSources.length > 0) {
        setLiveSources(initialSources);
      }
    }
  }, [isAgentMessage, message?.content, message?.isComplete, message?.metadata?.sources]);
  
  // Handle streaming content updates
  useEffect(() => {
    if (streamingContent && streamingContent.content && !streamingContent.isComplete) {
      setIsComplete(false);
      completionTriggered.current = false; // Reset completion flag for new streaming
      
      // Notify parent of streaming update for auto-scroll
      const currentLength = streamingContent.content.length;
      if (currentLength > previousContentLength.current) {
        triggerStreamingUpdate();
        previousContentLength.current = currentLength;
      }
      
    } else if (streamingContent && streamingContent.isComplete) {
      // Streaming content is explicitly marked as complete
      const wasStreaming = !isComplete;
      setIsComplete(true);
      
      // If we just completed streaming and haven't triggered completion yet
      if (wasStreaming && !completionTriggered.current && onStreamingComplete) {
        // console.log('🎯 STREAMING COMPLETED - triggering completion auto-scroll');
        completionTriggered.current = true;
        onStreamingComplete?.();
      }
    } else if (message?.content && !streamingContent) {
      // This is a regular message (not streaming), just mark as complete
      setIsComplete(true);
    }
  }, [streamingContent, message, triggerStreamingUpdate, isComplete, onStreamingComplete]);
  
  // Get the content to display with memoization
  const contentToDisplay = useMemo(() => {
    // For agent messages, prefer live content if available
    if (isAgentMessage && liveContent) {
      return liveContent;
    }
    
    // If we have a saved message, use that
    if (message?.content) {
      return message.content;
    }
    
    // Otherwise, if we have streaming content (complete or incomplete), show that
    if (streamingContent?.content) {
      return streamingContent.content;
    }
    
    // Fallback to empty
    return '';
  }, [isAgentMessage, liveContent, message?.content, streamingContent?.content]);

  // Get sources from live updates or message metadata
  const sources = useMemo(() => {
    // 🎯 FIX: Prioritize live sources from WebSocket updates
    if (isAgentMessage && liveSources.length > 0) {
      return liveSources;
    }
    
    // Fallback to message metadata sources
    const messageSources = message?.metadata?.sources || [];
    
    if (messageSources.length > 0) {
      return messageSources;
    }
    
    return [];
  }, [isAgentMessage, liveSources, message?.metadata?.sources]);
  

  
  // Handle security violations
  const handleSecurityViolation = (removedElements) => {
    console.warn('🚨 Security violation in streaming content:', removedElements);
    setHasSecurityWarning(true);
    // You could also notify parent component or send analytics
  };

  // Render agent message header
  const renderAgentHeader = () => {
    if (!isAgentMessage || !agentConfig) return null;
    const IconComponent = agentConfig.icon;
    
    return (
      <div 
        ref={headerRef}
        className={`flex items-center justify-between rounded-t-lg`}
      >
        <div className="flex items-center space-x-2">
          <IconComponent className={`h-4 w-4`} />
          <span className={`prose-sm font-medium`}>
            {agentConfig.label}
          </span>
        </div>
      </div>
    );
  };

  // Determine if this message is currently being streamed
  const isStreaming = useMemo(() => {
    // For agent messages, check live completion state
    if (isAgentMessage) {
      return !liveIsComplete && liveContent.length > 0;
    }
    
    // For regular messages, use existing logic
    return streamingContent && streamingContent.content && !isComplete;
  }, [isAgentMessage, liveIsComplete, liveContent, streamingContent, isComplete]);

  // Determine if this is a user message
  const isUserMessage = message?.role === 'user';
  const messageMeta = message?.message_metadata || message?.metadata || {};

  // Detect retryable error assistant messages
  const errorMetadata = message?.message_metadata || message?.metadata || {};
  const isProcessingError = errorMetadata?.error_type === 'processing_error';
  const isInsufficientTokens = errorMetadata?.response_status === 'insufficient_tokens';
  const hasTokensAvailable = typeof tokenBalance === 'number' && tokenBalance > 0;
  const contentLower = (contentToDisplay || '').toLowerCase().trim();
  const hasTryAgainHint = contentLower.includes('please try again');
  const showRetryButton = !!onRetry && !isUserMessage && (isProcessingError || hasTryAgainHint);

  // Scan-to-Play guests can't top up — their session simply ends. Make that a
  // graceful close (conversion moment) instead of a paywall they can't use.
  const isGuest = user?.authProvider === 'guest';
  const guestPartnerSlug = user?.app_settings?.guest_partner_slug || null;

  const contentForDisplay = isInsufficientTokens
    ? isGuest
      ? 'Deine kostenlose Session ist aufgebraucht — was für eine Tour! Komm morgen wieder vorbei und entdecke weiter.'
      : hasTokensAvailable
        ? "You're topped up. Continue where you left off."
        : "You're out of tokens. Top up to continue."
    : contentToDisplay;
  const showInsufficientTokensAction = !isUserMessage && isInsufficientTokens;
  
  return (
    <div className={`flex ${isUserMessage ? 'justify-end' : 'items-start space-x-3'}`} ref={messageContainerRef} style={{ overflowAnchor: 'auto' }}>
      {/* Message Content */}
      <div className={`${isUserMessage ? 'max-w-[75%]' : 'flex-1 min-w-0'} transition-all duration-300 ease-in-out ${agentMessageType === 'tools' ? 'border p-2 rounded-lg' : ''} ${isUserMessage ? 'bg-gray-200 dark:bg-gray-600/40 text-gray-900 dark:text-gray-100 px-3.5 py-2 rounded-2xl' : ''}`}>
        {/* Agent Message Container */}
         
        {isAgentMessage ? (
          <>

            
            <div className={`${agentMessageType === 'working' ? '' : 'mb-'}`}>
              {agentMessageType !== "working" && (
                renderAgentHeader()
              )}
              {/* Content */}
              <div 
                  className="pt-2" 
                  ref={contentAreaRef}
                >
                  {/* Security Warning */}
                  {hasSecurityWarning && (
                    <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Security filter activated - some content was blocked
                      </p>
                    </div>
                  )}
                  
                  {/* Image Display */}
                  {messageMeta?.imageUrl && (
                    <div className="mb-3">
                      <img 
                        src={getImageUrl(messageMeta.imageUrl)} 
                        alt="Shared content" 
                        className="max-w-full w-auto h-auto max-h-64 rounded-lg border border-gray-300 object-cover cursor-zoom-in"
                        onClick={() => {
                          if (typeof onImageClick === 'function') {
                            onImageClick({ message });
                          }
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.error('Failed to load image:', messageMeta.imageUrl);
                        }}
                      />
                    </div>
                  )}

                  {/* Message Content with Inline Citations */}
                  {contentForDisplay && (
                    <div 
                      ref={streamingRef}
                      className={`prose prose-sm prose-gray dark:prose-invert max-w-none prose-p:leading-relaxed prose-headings:font-semibold prose-headings:tracking-tight ${
                        isStreaming ? '' : ''
                      }`}
                    >
                      {/*  content rendering */}
                      <div className="markdown-content">
                        <StreamingContentRenderer 
                          content={contentForDisplay}
                          isStreaming={isStreaming}
                          onSecurityViolation={handleSecurityViolation}
                          sources={sources}
                          agentMessageType={agentMessageType}
                        />
                      </div>
                    </div>
                  )}

                  {showInsufficientTokensAction && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {isGuest ? (
                        guestPartnerSlug && (
                          <Link
                            to={`/p/${guestPartnerSlug}`}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/40 dark:text-emerald-300 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                          >
                            Mehr entdecken
                          </Link>
                        )
                      ) : hasTokensAvailable ? (
                        <button
                          type="button"
                          onClick={() => onInsufficientTokensContinue && onInsufficientTokensContinue(message)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/40 dark:text-emerald-300 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                        >
                          Continue
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onInsufficientTokensTopUp && onInsufficientTokensTopUp(message)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/40 dark:text-emerald-300 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                        >
                          Top up
                        </button>
                      )}
                    </div>
                  )}

                  {showRetryButton && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => onRetry && onRetry(message)}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                </div>
            </div>
          </>
        ) : (
          /* User Message Rendering - clean, compact bubble */
          <>
            {hasSecurityWarning && (
              <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Security filter activated - some content was blocked
                </p>
              </div>
            )}
            
            {/* Image Display for user messages */}
            {messageMeta?.imageUrl && (
              <div className="mb-1">
                <img 
                  src={getImageUrl(messageMeta.imageUrl)} 
                  alt="Shared content" 
                  className="max-w-full w-auto h-auto max-h-64 rounded-xl mt-1 border border-gray-300 dark:border-gray-600 object-cover cursor-zoom-in"
                  onClick={() => {
                    if (typeof onImageClick === 'function') {
                      onImageClick({ message });
                    }
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    console.error('Failed to load image:', messageMeta.imageUrl);
                  }}
                />
              </div>
            )}
            
            {/* User message text - no prose to avoid extra margins.
                Spoken turns (audioUrl) get a small mic marker; the bubble text
                is the transcript itself (agent-transcribed server-side). */}
            {contentForDisplay && (
              <div 
                ref={streamingRef}
                className={`text-[14px] leading-relaxed [&_p]:m-0 [&_p:not(:last-child)]:mb-1 ${
                  isUserMessage && messageMeta?.audioUrl ? 'flex items-start gap-1.5' : ''
                }`}
              >
                {isUserMessage && messageMeta?.audioUrl && (
                  <Mic
                    size={14}
                    className="flex-shrink-0 mt-1.5 opacity-60"
                    aria-label="Spoken message"
                  />
                )}
                <StreamingContentRenderer 
                  content={contentForDisplay}
                  isStreaming={isStreaming}
                  onSecurityViolation={handleSecurityViolation}
                  sources={sources}
                  agentMessageType={agentMessageType}
                />
              </div>
            )}

            {/* Voice layer V1: listen button on completed assistant messages.
                Narrates the message text itself (sanitized server-side) —
                screen and voice are always the same words. */}
            {!isUserMessage &&
              voicePlayback?.enabled &&
              !isStreaming &&
              !message?._isStreaming &&
              !isInsufficientTokens &&
              message?.id &&
              (contentForDisplay || '').trim().length > 30 && (
                <div className="mt-1.5 -ml-1.5">
                  <VoiceListenButton
                    text={contentForDisplay}
                    playbackKey={`msg_${message.id}`}
                    projectId={voicePlayback.projectId}
                    chatId={voicePlayback.chatId}
                    messageId={message.id}
                  />
                </div>
              )}

            {showInsufficientTokensAction && (
              <div className="mt-3 flex flex-wrap gap-2">
                {isGuest ? (
                  guestPartnerSlug && (
                    <Link
                      to={`/p/${guestPartnerSlug}`}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/40 dark:text-emerald-300 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                    >
                      Mehr entdecken
                    </Link>
                  )
                ) : hasTokensAvailable ? (
                  <button
                    type="button"
                    onClick={() => onInsufficientTokensContinue && onInsufficientTokensContinue(message)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/40 dark:text-emerald-300 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onInsufficientTokensTopUp && onInsufficientTokensTopUp(message)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/40 dark:text-emerald-300 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20"
                  >
                    Top up
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

StreamingMessage.displayName = 'StreamingMessage';

// Wrapper component for regular messages (non-streaming)
export const RegularMessage = React.memo(({ message, onCitationClick, citationRef, onStreamingUpdate, onStreamingComplete }) => {
  return (
    <StreamingMessage
      message={message}
      streamingContent={null}
      onCitationClick={onCitationClick}
      citationRef={citationRef}
      onStreamingUpdate={onStreamingUpdate}
      onStreamingComplete={onStreamingComplete}
    />
  );
});

RegularMessage.displayName = 'RegularMessage';

export default StreamingMessage; 